import { ReactNode, useEffect, useState } from "react";
import { ArrowUpRight, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import {
  consumeStaffBypassFromUrl,
  hasStaffBypass,
  isGatedHost,
} from "@/lib/demoGate";

// Consume the staff flag before the first render so approved staff never see
// the construction screen flash before the working site appears.
if (typeof window !== "undefined") {
  consumeStaffBypassFromUrl();
}

type Props = { children: ReactNode };

/**
 * Locks every public route on the production domains while the site is being
 * completed. Staff can enter through ?staff=1, and active users retain access.
 */
export const DemoGate = ({ children }: Props) => {
  const { user, loading } = useAuth();
  const [staffAccess, setStaffAccess] = useState(() => hasStaffBypass());

  useEffect(() => {
    setStaffAccess(hasStaffBypass());
  }, []);

  if (!isGatedHost() || staffAccess || (!loading && user)) {
    return <>{children}</>;
  }

  return <ConstructionNotice />;
};

const ConstructionNotice = () => (
  <main className="relative flex min-h-[100svh] flex-col overflow-hidden bg-background px-6 text-foreground sm:px-10 lg:px-14">
    <header className="flex h-[66px] shrink-0 items-center justify-between border-b border-hairline">
      <span className="font-serif-display text-lg leading-none">CASEY</span>
      <div className="label-uppercase flex items-center gap-2 text-foreground">
        <LockKeyhole className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden="true" />
        Site update
      </div>
    </header>

    <section className="mx-auto flex w-full max-w-[1360px] flex-1 items-center py-16 sm:py-20">
      <div className="grid w-full gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.32fr)] lg:items-end lg:gap-20">
        <div>
          <p className="eyebrow mb-8">Still under construction</p>
          <h1 className="max-w-5xl text-[clamp(3.4rem,8.6vw,8rem)] leading-[0.94]">
            We are building
            <span className="block">Casey with <em>care.</em></span>
          </h1>
        </div>

        <div className="border-t border-hairline pt-6 lg:mb-2">
          <p className="max-w-sm text-base leading-7 text-muted-foreground">
            Casey Legal Tools is currently being refined before the site reopens.
          </p>
          <Button asChild className="mt-8 h-11 rounded-full px-6">
            <a href="mailto:info@caseylegaltools.com">
              Contact us
              <ArrowUpRight aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>
    </section>

    <footer className="relative h-[clamp(5rem,13vw,11rem)] shrink-0 border-t border-hairline" aria-hidden="true">
      <span className="absolute left-0 top-4 font-serif-display text-[clamp(5rem,18vw,16rem)] leading-[0.72] text-foreground">
        CASEY
      </span>
    </footer>
  </main>
);